import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  ArrowRight,
  Loader2,
  QrCode,
  Plus,
  CheckCircle2,
  AlertCircle,
  Ticket,
  ChevronLeft,
  Clock,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@/components/pos/EmptyState';
import { RepurchaseAddOns } from '@/components/pos/RepurchaseAddOns';
import { ManagerApprovalDialog } from '@/components/pos/ManagerApprovalDialog';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { getProducts } from '@/api/product.api';
import { formatTime } from '@/lib/format';
import { SLOT_NONE, selectableSlots, slotTimeLabel } from '@/lib/slots';
import {
  CAP_REACHED_LABEL,
  buildHeldCounts,
  capOfProduct,
  capReachedSlotIds,
  isCapped,
} from '@/lib/purchaseCap';
import { cn } from '@/lib/utils';
import {
  resolveWristband,
  transferAddOn,
  rescheduleAddOn,
  looksLikeWristbandToken,
  pickTransferChallenge,
  resolveAddOnsForQr,
  makeAddOnCompositeId,
  isAddOnTransferable,
  isAddOnReschedulable,
  type ResolvedWristband,
  type WristbandAddOn,
} from '@/api/wristband.api';

type Step =
  | 'scan'
  | 'overview'
  | 'transfer_select'
  | 'transfer_dest'
  | 'repurchase'
  | 'reschedule_select'
  | 'reschedule_slot';

interface Party {
  resolved: ResolvedWristband;
  token: string;
  challenge: string | null;
}

/**
 * /manage-addons — cashier Transfer / Repurchase Add-ons flow.
 *
 * A single-page wizard over the public /qr/* + /sale/addon surface:
 *
 *   SCAN → OVERVIEW ─┬─ TRANSFER_SELECT → TRANSFER_DEST → confirm → OVERVIEW
 *                    └─ REPURCHASE (pay via POS dialog) → OVERVIEW
 *
 * Identity is the AP2 wristband token's HMAC; the cashier scans a wristband to
 * pull its add-ons, then either moves selected add-ons to another wristband in
 * the same booking, or sells more add-ons against it.
 */
export function ManageAddonsPage() {
  const [step, setStep] = useState<Step>('scan');
  const [source, setSource] = useState<Party | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmDest, setConfirmDest] = useState<Party | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Reschedule flow: the picked add-on, the chosen target slot, and the manager
  // credential-override dialog.
  const [rescheduleTarget, setRescheduleTarget] = useState<WristbandAddOn | null>(null);
  const [rescheduleSlotId, setRescheduleSlotId] = useState<string | null>(null);
  const [approvalOpen, setApprovalOpen] = useState(false);

  const addOns = useMemo(
    () => (source ? resolveAddOnsForQr(source.resolved) : []),
    [source],
  );
  const transferable = useMemo(
    () => addOns.filter((a) => isAddOnTransferable(a)),
    [addOns],
  );
  const reschedulable = useMemo(
    () => addOns.filter((a) => isAddOnReschedulable(a)),
    [addOns],
  );

  // The authoritative visit date is qrCode.effectiveDate — a Jakarta
  // "YYYY-MM-DD" (the exact field the BE add-on/transfer gates compare to
  // today). Prefer it. Only if it's absent do we fall back to sale.visitDate,
  // which is a UTC INSTANT — convert it to the Jakarta calendar date rather
  // than slicing the ISO (a slice lands on the previous day in the UTC-evening
  // window and would falsely block a valid same-day repurchase).
  const visitDate = useMemo(() => {
    const eff = source?.resolved?.qrCode?.effectiveDate;
    if (eff) return String(eff).slice(0, 10);
    const iso = source?.resolved?.sale?.visitDate;
    if (!iso) return null;
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta' }).format(new Date(iso));
  }, [source]);
  const wrongDate = useMemo(() => {
    if (!visitDate) return false;
    const jkt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta' }).format(new Date());
    return visitDate !== jkt;
  }, [visitDate]);

  const resetAll = () => {
    setSource(null);
    setSelectedIds([]);
    setStep('scan');
  };

  const onSourceResolved = (party: Party) => {
    setSource(party);
    setSelectedIds([]);
    setStep('overview');
  };

  const selectedRefs = useMemo(() => {
    const byId = new Map(addOns.map((a, i) => [makeAddOnCompositeId(a, i), a] as const));
    return selectedIds
      .map((id) => byId.get(id))
      .filter((a): a is WristbandAddOn => !!a && !!a.saleItemId)
      .map((a) => ({
        saleItemId: String(a.saleItemId),
        productId: a.productId ? String(a.productId) : undefined,
        quantityIndex: Number(a.quantityIndex ?? 1),
      }));
  }, [addOns, selectedIds]);

  const doTransfer = async (dest: Party) => {
    if (!source) return;
    setSubmitting(true);
    try {
      const res = await transferAddOn({
        fromQrToken: source.token,
        toQrToken: dest.token,
        addOnRefs: selectedRefs,
        fromChallenge: source.challenge,
        toChallenge: dest.challenge,
      });
      const n = res.transferredCount ?? selectedRefs.length;
      toast.success(`${n} add-on dipindahkan.`);
      setConfirmOpen(false);
      // Re-resolve the source so its add-on list reflects the move.
      const refreshed = await resolveWristband(source.token);
      setSource({ ...source, resolved: refreshed, challenge: pickTransferChallenge(refreshed) });
      setSelectedIds([]);
      setStep('overview');
    } catch (err) {
      toast.error((err as Error)?.message || 'Gagal memindahkan add-on.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reschedule the picked add-on to `rescheduleSlotId` (same-date slot of the
   * same product). Authorized by a manager's token from the approval dialog —
   * reuses the admin reschedule endpoint, which enforces the permission + audits.
   */
  const doReschedule = async (managerToken: string) => {
    const saleId = source?.resolved?.sale?._id;
    const parentQrId = source?.resolved?.qrCode?._id;
    const addOnId = rescheduleTarget?._id;
    if (!saleId || !parentQrId || !addOnId || !rescheduleSlotId) {
      toast.error('Data jadwal ulang tidak lengkap.');
      return;
    }
    setSubmitting(true);
    try {
      await rescheduleAddOn(
        { saleId, parentQrId, addOnId, targetSlotId: rescheduleSlotId },
        managerToken,
      );
      toast.success('Add-on berhasil dijadwalkan ulang.');
      setApprovalOpen(false);
      setRescheduleTarget(null);
      setRescheduleSlotId(null);
      // Re-resolve so the add-on shows its new slot time.
      if (source) {
        const refreshed = await resolveWristband(source.token);
        setSource({ ...source, resolved: refreshed, challenge: pickTransferChallenge(refreshed) });
      }
      setStep('overview');
    } catch (err) {
      toast.error((err as Error)?.message || 'Gagal menjadwalkan ulang add-on.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-6 overflow-y-auto px-6 py-8 pos-scroll">
      <header>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-ink-900">
          <ArrowLeftRight className="h-6 w-6 text-crimson-700" /> Kelola Add-on
        </h1>
        <p className="text-sm text-muted-foreground">
          Pindai gelang untuk memindahkan atau menjual add-on tambahan.
        </p>
      </header>

      {step === 'scan' && (
        <ScanPanel
          key="source-scan"
          title="Pindai gelang"
          hint="Arahkan QR gelang pelanggan ke pemindai untuk mulai."
          onResolved={onSourceResolved}
        />
      )}

      {step === 'overview' && source && (
        <Overview
          resolved={source.resolved}
          addOns={addOns}
          transferableCount={transferable.length}
          reschedulableCount={reschedulable.length}
          wrongDate={wrongDate}
          onTransfer={() => {
            setSelectedIds([]);
            setStep('transfer_select');
          }}
          onReschedule={() => {
            setRescheduleTarget(null);
            setRescheduleSlotId(null);
            setStep('reschedule_select');
          }}
          onRepurchase={() => setStep('repurchase')}
          onRescan={resetAll}
        />
      )}

      {step === 'transfer_select' && source && (
        <TransferSelect
          addOns={addOns}
          selectedIds={selectedIds}
          onToggle={(id) =>
            setSelectedIds((cur) =>
              cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
            )
          }
          onBack={() => setStep('overview')}
          onNext={() => setStep('transfer_dest')}
        />
      )}

      {step === 'transfer_dest' && source && (
        <div className="space-y-4">
          <ScanPanel
            key="dest-scan"
            title="Pindai gelang tujuan"
            hint="Gelang tujuan harus dari pesanan (invoice) yang sama."
            excludeToken={source.token}
            onResolved={(dest) => {
              setConfirmDest(dest);
              setConfirmOpen(true);
            }}
            onBack={() => setStep('transfer_select')}
          />
          <ConfirmTransferDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            count={selectedRefs.length}
            submitting={submitting}
            onConfirm={() => confirmDest && doTransfer(confirmDest)}
          />
        </div>
      )}

      {step === 'repurchase' && source && (
        <RepurchaseAddOns
          token={source.token}
          visitDate={visitDate}
          wrongDate={wrongDate}
          heldAddOns={addOns}
          onBack={() => setStep('overview')}
          onPaid={async () => {
            const refreshed = await resolveWristband(source.token);
            setSource({ ...source, resolved: refreshed, challenge: pickTransferChallenge(refreshed) });
            setStep('overview');
          }}
        />
      )}

      {step === 'reschedule_select' && source && (
        <RescheduleSelect
          addOns={reschedulable}
          onBack={() => setStep('overview')}
          onPick={(a) => {
            setRescheduleTarget(a);
            setRescheduleSlotId(null);
            setStep('reschedule_slot');
          }}
        />
      )}

      {step === 'reschedule_slot' && source && rescheduleTarget && (
        <>
          <RescheduleSlot
            addOn={rescheduleTarget}
            date={visitDate}
            heldAddOns={addOns}
            selectedSlotId={rescheduleSlotId}
            onSelectSlot={setRescheduleSlotId}
            onBack={() => setStep('reschedule_select')}
            onReschedule={() => setApprovalOpen(true)}
          />
          <ManagerApprovalDialog
            open={approvalOpen}
            onOpenChange={setApprovalOpen}
            title="Persetujuan manajer"
            description="Menjadwalkan ulang add-on memindahkan kapasitas slot dan tercatat. Minta supervisor menyetujui."
            submitting={submitting}
            onApprove={doReschedule}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scan panel — scan-ONLY viewfinder (no text field / button). A hidden,
// always-focused input receives the USB scanner's keystrokes and auto-submits
// on the trailing Enter, exactly like the kiosk capture surface.
// ---------------------------------------------------------------------------

function ScanPanel({
  title,
  hint,
  excludeToken,
  onResolved,
  onBack,
}: {
  title: string;
  hint: string;
  excludeToken?: string;
  onResolved: (party: Party) => void;
  onBack?: () => void;
}) {
  const [buffer, setBuffer] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const resolve = async (raw: string) => {
    const t = raw.trim();
    if (!t || busy) return;
    if (!looksLikeWristbandToken(t)) {
      toast.error('Itu bukan kode gelang yang valid (AP2…).');
      return;
    }
    if (excludeToken && t === excludeToken) {
      toast.error('Gelang tujuan tidak boleh sama dengan sumber.');
      return;
    }
    setBusy(true);
    try {
      const resolved = await resolveWristband(t);
      onResolved({ resolved, token: t, challenge: pickTransferChallenge(resolved) });
    } catch (err) {
      toast.error((err as Error)?.message || 'Gelang tidak ditemukan.');
    } finally {
      setBusy(false);
    }
  };

  // Keep the hidden field focused so the scanner's keystrokes always land here.
  // Re-focus on an interval + on window focus + on blur — a scan can never miss.
  useEffect(() => {
    const focus = () => {
      if (!busy) inputRef.current?.focus();
    };
    focus();
    const id = window.setInterval(focus, 1000);
    window.addEventListener('focus', focus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', focus);
    };
  }, [busy]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = buffer;
    setBuffer('');
    resolve(t);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {onBack && (
          <div className="mb-1 flex justify-end">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Kembali
            </button>
          </div>
        )}

        <form
          onSubmit={submit}
          onClick={() => inputRef.current?.focus()}
          className="mx-auto flex max-w-md cursor-pointer flex-col items-center gap-5 py-6 text-center"
        >
          {/* Viewfinder medallion — gold rim, corner brackets, sweeping scan line */}
          <div className="relative flex h-40 w-40 items-center justify-center overflow-hidden rounded-3xl border-[3px] border-gold-500 bg-gradient-to-br from-crimson-700/10 to-crimson-900/5 shadow-[inset_0_0_22px_rgba(230,184,0,0.18)]">
            <QrCode className="h-20 w-20 text-crimson-700" strokeWidth={1.5} aria-hidden="true" />
            {!busy && (
              <span className="scan-line pointer-events-none absolute inset-x-[10%] h-[3px] rounded-full bg-gradient-to-r from-transparent via-gold-500 to-transparent shadow-[0_0_10px_var(--gold-500)]" />
            )}
            {/* corner brackets */}
            <span className="absolute left-2 top-2 h-4 w-4 rounded-tl border-l-[3px] border-t-[3px] border-gold-500" />
            <span className="absolute right-2 top-2 h-4 w-4 rounded-tr border-r-[3px] border-t-[3px] border-gold-500" />
            <span className="absolute bottom-2 left-2 h-4 w-4 rounded-bl border-b-[3px] border-l-[3px] border-gold-500" />
            <span className="absolute bottom-2 right-2 h-4 w-4 rounded-br border-b-[3px] border-r-[3px] border-gold-500" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-ink-900">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
          </div>

          {/* Live status — ready pulse, or reading spinner */}
          <div className="flex min-h-[28px] items-center gap-2">
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-crimson-700" />
                <span className="text-sm font-semibold text-crimson-700">Membaca gelang…</span>
              </>
            ) : (
              <>
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-500" />
                <span className="text-sm font-semibold text-ink-600">
                  Siap — arahkan QR gelang ke pemindai
                </span>
              </>
            )}
          </div>

          {/* Hidden capture field — scanner types here, Enter submits the form.
              Off-screen + not user-typeable; manual keying is not offered. */}
          <input
            ref={inputRef}
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onBlur={() => {
              if (!busy) inputRef.current?.focus();
            }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label={title}
            tabIndex={-1}
            className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
          />
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wristband overview — parent ticket + add-on list + branch actions.
// ---------------------------------------------------------------------------

function AddOnRow({ addon, index, selectable, selected, onToggle }: {
  addon: WristbandAddOn;
  index: number;
  selectable?: boolean;
  selected?: boolean;
  onToggle?: () => void;
}) {
  const transferable = isAddOnTransferable(addon);
  const name = addon.productName || addon.name || 'Add-on';
  const slot =
    addon.slotStartAt && addon.slotEndAt
      ? `${formatTime(addon.slotStartAt)}–${formatTime(addon.slotEndAt)}`
      : null;
  const statusLabel = addon.isEntered
    ? 'Sudah masuk'
    : addon.status === 'REFUNDED'
      ? 'Refund'
      : transferable
        ? 'Tersedia'
        : 'Terpakai';

  const body = (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors',
        selectable && transferable && 'cursor-pointer hover:border-crimson-300',
        selected ? 'border-crimson-500 bg-crimson-50' : 'border-cream-200 bg-white',
        selectable && !transferable && 'opacity-50',
      )}
    >
      {selectable && (
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2',
            selected ? 'border-crimson-600 bg-crimson-600 text-white' : 'border-cream-300',
          )}
        >
          {selected && <CheckCircle2 className="h-4 w-4" />}
        </span>
      )}
      <Ticket className="h-5 w-5 shrink-0 text-crimson-600" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink-900">{name}</p>
        {slot ? (
          <p className="flex items-center gap-1 text-xs text-ink-500">
            <Clock className="h-3 w-3 shrink-0" /> {slot}
          </p>
        ) : (
          <p className="text-xs text-ink-400">Tanpa jam kunjungan</p>
        )}
      </div>
      <Badge
        variant={transferable ? 'secondary' : 'outline'}
        className={cn(transferable ? 'bg-green-100 text-green-700' : 'text-ink-400')}
      >
        {statusLabel}
      </Badge>
    </div>
  );

  if (selectable && transferable && onToggle) {
    return (
      <button type="button" onClick={onToggle} className="block w-full text-left">
        {body}
      </button>
    );
  }
  return <div key={index}>{body}</div>;
}

function Overview({
  resolved,
  addOns,
  transferableCount,
  reschedulableCount,
  wrongDate,
  onTransfer,
  onReschedule,
  onRepurchase,
  onRescan,
}: {
  resolved: ResolvedWristband;
  addOns: WristbandAddOn[];
  transferableCount: number;
  reschedulableCount: number;
  wrongDate: boolean;
  onTransfer: () => void;
  onReschedule: () => void;
  onRepurchase: () => void;
  onRescan: () => void;
}) {
  const parentName = resolved.parentSaleItem?.productName || 'Tiket masuk';
  const invoiceId = resolved.sale?.invoiceId;
  const label = resolved.qrCode?.label;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Gelang</p>
              <p className="truncate font-display text-lg font-bold text-ink-900">{parentName}</p>
              <p className="mt-0.5 text-sm text-ink-400">
                {label ? `${label} · ` : ''}
                {invoiceId ?? '—'}
              </p>
            </div>
            <button
              type="button"
              onClick={onRescan}
              className="shrink-0 text-xs font-medium text-crimson-700 hover:underline"
            >
              Pindai gelang lain
            </button>
          </div>
          {wrongDate && (
            <p className="mt-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Tanggal kunjungan gelang ini bukan hari ini — pembelian add-on tidak tersedia.
            </p>
          )}
        </CardContent>
      </Card>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
          Add-on pada gelang ini ({addOns.length})
        </p>
        {addOns.length === 0 ? (
          <EmptyState
            icon={Ticket}
            title="Belum ada add-on"
            description="Gelang ini belum memiliki add-on. Anda bisa menjual add-on baru."
            className="border-0 bg-transparent"
          />
        ) : (
          <div className="space-y-2">
            {addOns.map((a, i) => (
              <AddOnRow key={makeAddOnCompositeId(a, i)} addon={a} index={i} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Button
          size="lg"
          variant="outline"
          className="h-14 gap-2 text-base"
          disabled={transferableCount === 0}
          onClick={onTransfer}
        >
          <ArrowLeftRight className="h-5 w-5" />
          Transfer Add-on
        </Button>
        <Button
          size="lg"
          className="h-14 gap-2 text-base"
          disabled={wrongDate}
          onClick={onRepurchase}
        >
          <Plus className="h-5 w-5" />
          Beli Add-on
        </Button>
      </div>
      <Button
        size="lg"
        variant="outline"
        className="h-14 w-full gap-2 text-base"
        disabled={reschedulableCount === 0}
        onClick={onReschedule}
      >
        <CalendarClock className="h-5 w-5" />
        Jadwalkan Ulang Add-on
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reschedule select — pick ONE add-on to move to a different same-date slot.
// ---------------------------------------------------------------------------

function RescheduleSelect({
  addOns,
  onBack,
  onPick,
}: {
  addOns: WristbandAddOn[];
  onBack: () => void;
  onPick: (a: WristbandAddOn) => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-ink-900">
              Pilih add-on untuk dijadwalkan ulang
            </p>
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Kembali
            </button>
          </div>
          {addOns.length === 0 ? (
            <p className="text-sm text-ink-400">
              Tidak ada add-on yang dapat dijadwalkan ulang.
            </p>
          ) : (
            <div className="space-y-2">
              {addOns.map((a, i) => (
                <button
                  key={makeAddOnCompositeId(a, i)}
                  type="button"
                  onClick={() => onPick(a)}
                  className="block w-full text-left transition-transform hover:-translate-y-0.5"
                >
                  <AddOnRow addon={a} index={i} />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reschedule slot — pick the new SAME-DATE slot for the chosen add-on.
// ---------------------------------------------------------------------------

function RescheduleSlot({
  addOn,
  date,
  heldAddOns,
  selectedSlotId,
  onSelectSlot,
  onBack,
  onReschedule,
}: {
  addOn: WristbandAddOn;
  date: string | null;
  /** All add-ons on the wristband — feeds the purchase-cap gate on target slots. */
  heldAddOns: WristbandAddOn[];
  selectedSlotId: string | null;
  onSelectSlot: (id: string | null) => void;
  onBack: () => void;
  onReschedule: () => void;
}) {
  const productId = addOn.productId ? String(addOn.productId) : '';
  const effDate = date || new Date().toISOString().slice(0, 10);

  const availability = useQuery({
    queryKey: ['reschedule-availability', productId, effDate],
    enabled: Boolean(productId),
    queryFn: () => getProductAvailability(productId, effDate),
  });
  // Same-date OPEN slots, minus the add-on's current slot (can't move to itself).
  const slots = selectableSlots(availability.data?.slots ?? []).filter(
    (s) => s.id !== addOn.slotId,
  );

  // ── Purchase-cap gate ──────────────────────────────────────────────────
  // The cap lives on the product (attraction), not the add-on payload; pull it
  // from the catalog. Cached under the same key RepurchaseAddOns uses.
  const productsQuery = useQuery({
    queryKey: ['addon-products'],
    queryFn: async () => {
      const res = await getProducts({ per_page: 100, page_number: 1 });
      return res.items.filter(
        (p) => (p.ticketCategory ?? '').toLowerCase() === 'attraction' && p.status !== 'INACTIVE',
      );
    },
  });
  const capProduct = productsQuery.data?.find((p) => String(p.id) === productId);
  const cap = capOfProduct(capProduct);
  const attractionId = addOn.attractionId ?? capProduct?.attractionId;
  // Held excludes the add-on being moved (it releases its current slot), so it
  // never blocks the customer's own re-pick. Reference identity is stable here.
  const held = buildHeldCounts((heldAddOns ?? []).filter((a) => a !== addOn));
  const blockedSlotIds = isCapped(cap)
    ? capReachedSlotIds({ cap, attractionId, slots, held, cart: null })
    : new Set<string>();

  const name = addOn.productName || addOn.name || 'Add-on';
  const currentSlot =
    addOn.slotStartAt && addOn.slotEndAt
      ? `${formatTime(addOn.slotStartAt)}–${formatTime(addOn.slotEndAt)}`
      : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Kembali
          </button>

          <div>
            <p className="font-semibold text-ink-900">{name}</p>
            {currentSlot && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-400">
                <Clock className="h-3 w-3" /> Jadwal saat ini: {currentSlot}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
              <CalendarClock className="h-3.5 w-3.5" /> Jadwal baru (hari yang sama)
            </p>
            {availability.isLoading ? (
              <div className="flex py-2 text-ink-400">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-amber-600">
                Tidak ada slot lain yang tersedia pada tanggal ini.
              </p>
            ) : (
              <Select
                value={selectedSlotId ?? SLOT_NONE}
                onValueChange={(v) => onSelectSlot(v === SLOT_NONE ? null : v)}
              >
                <SelectTrigger aria-label="Jadwal baru">
                  <SelectValue placeholder="Pilih jadwal baru" />
                </SelectTrigger>
                <SelectContent>
                  {slots.map((s) => {
                    const capBlocked = blockedSlotIds.has(s.id);
                    return (
                      <SelectItem key={s.id} value={s.id} disabled={capBlocked}>
                        {slotTimeLabel(s)}
                        {capBlocked ? ` · ${CAP_REACHED_LABEL}` : ` · Sisa ${s.availableCount}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      <Button
        size="lg"
        className="h-14 w-full gap-2 text-lg"
        disabled={!selectedSlotId}
        onClick={onReschedule}
      >
        <CalendarClock className="h-5 w-5" /> Jadwalkan Ulang
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transfer select — pick which add-ons to move.
// ---------------------------------------------------------------------------

function TransferSelect({
  addOns,
  selectedIds,
  onToggle,
  onBack,
  onNext,
}: {
  addOns: WristbandAddOn[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-ink-900">Pilih add-on untuk dipindahkan</p>
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-xs font-medium text-crimson-700 hover:underline"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Kembali
            </button>
          </div>
          <div className="space-y-2">
            {addOns.map((a, i) => {
              const id = makeAddOnCompositeId(a, i);
              return (
                <AddOnRow
                  key={id}
                  addon={a}
                  index={i}
                  selectable
                  selected={selectedIds.includes(id)}
                  onToggle={() => onToggle(id)}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Button
        size="lg"
        className="h-14 w-full gap-2 text-lg"
        disabled={selectedIds.length === 0}
        onClick={onNext}
      >
        Lanjut · {selectedIds.length} dipilih
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  );
}

function ConfirmTransferDialog({
  open,
  onOpenChange,
  count,
  submitting,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  count: number;
  submitting: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pindahkan {count} add-on?</DialogTitle>
          <DialogDescription>
            Add-on yang dipilih akan berpindah ke gelang tujuan. Tindakan ini tidak bisa dibatalkan
            otomatis.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={onConfirm} disabled={submitting} className="gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
            Pindahkan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
